import _isEmpty from 'lodash/isEmpty';
import { PENDING_ITEMS_TYPES } from '../../common/side-panel/pending-items/constants';
import type { AnnotationLabelT } from '../../common/types/types';
import { isAnnotationPending } from '../../common/utils/questionnaire-validation';
import type { CachedQuestionnaireStatus } from '../../modernization-common/side-panel/atoms/questionnaire.atom';
import type { EntityTaggingAnnotationT, EntityTaggingSidePanelAnnotationT } from '../types';
const INCOMPLETE_QUESTIONNAIRE = 'Incomplete questionnaire.';
type PendingAnnotationT = {
  type: (typeof PENDING_ITEMS_TYPES)[keyof typeof PENDING_ITEMS_TYPES];
  message: string;
  annotationId: string;
  annotationLabel: AnnotationLabelT;
};
export const getEntityTaggingPendingItems = (filteredAnnotations: EntityTaggingSidePanelAnnotationT[], questionnaireCache: Record<string, CachedQuestionnaireStatus>, validations: {
  maxSegmentLength?: number;
  minSegmentLength?: number;
}): PendingAnnotationT[] => {
  const genericAnnotations = filteredAnnotations.map(annotation => ({
    id: annotation.id,
    annotationType: annotation.annotationType,
    annotationLabel: annotation.annotationLabel,
    metadata: annotation.metadata,
    data: annotation.data
  }));
  // TODO: Add Task Level Questionnaire Pending items, will require changes to the return type
  return getEntityTaggingPendingAnnotations(genericAnnotations, questionnaireCache, validations);
};
const validateEntityTaggingAnnotationPending = (annotation: EntityTaggingAnnotationT & {
  id: string;
}, validations: {
  maxSegmentLength?: number;
  minSegmentLength?: number;
}): {
  error: true;
  message: string;
} | {
  error: false;
} => {
  const {
    maxSegmentLength,
    minSegmentLength
  } = validations;
  const {
    data
  } = annotation;
  const rangeWithIssue = data.find(range => {
    const rangeLength = range.end - range.start;
    // Float comparison is not accurate, so we need to use a small epsilon to account for the precision loss.
    const epsilon = 0.00001;
    if (minSegmentLength && rangeLength < minSegmentLength - epsilon) {
      return true;
    }
    if (maxSegmentLength && rangeLength > maxSegmentLength + epsilon) {
      return true;
    }
    return false;
  });
  if (rangeWithIssue) {
    return {
      error: true,
      message: `Invalid Segment Length: Segments must be between ${minSegmentLength}-${maxSegmentLength} seconds. Please split or merge the slots to meet the allowed range`
    };
  }
  return {
    error: false
  };
};
export const getEntityTaggingPendingAnnotations = (annotationList: (EntityTaggingAnnotationT & {
  id: string;
})[], questionnaireCache: Record<string, CachedQuestionnaireStatus> = {}, validations: {
  maxSegmentLength?: number;
  minSegmentLength?: number;
}) => {
  if (!annotationList) return [];
  return annotationList.reduce<Array<PendingAnnotationT>>((acc, annotation) => {
    const {
      annotationLabel,
      id
    } = annotation;
    const isEntityTaggingAnnotationPending = validateEntityTaggingAnnotationPending(annotation, validations);
    if (isEntityTaggingAnnotationPending.error) {
      acc.push({
        type: PENDING_ITEMS_TYPES.Custom,
        message: isEntityTaggingAnnotationPending.message,
        annotationId: id,
        annotationLabel
      });
    }
    if (!_isEmpty(annotationLabel.questionnaire)) {
      const isPending = isAnnotationPending(annotation, questionnaireCache);
      if (isPending) {
        acc.push({
          type: PENDING_ITEMS_TYPES.Questionnaire,
          message: INCOMPLETE_QUESTIONNAIRE,
          annotationId: id,
          annotationLabel
        });
      }
    }
    return acc;
  }, []);
};